import { Injectable } from '@angular/core';
import {
  KbAnalyseItemResponse,
  KbDiseaseItemResponse,
  KbGeneticDictionaryItemResponse,
  KbItemListResponse,
  KbItemType,
  KnowledgeBaseClient,
} from '@medindex-webapi';
import { SearchPanelModel } from '../interfaces/helix-book.interfaces';
import {
  BehaviorSubject,
  Observable,
  combineLatest,
  filter,
  first,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { StorageService } from '@shared/core/services/storage.service';
import { AddCartItemService } from '@shared/modules/add-cart-item/services/add-cart-item.service';
import { YmGoalHelixbook } from '../enums/ym-goal-helixbook.enum';

@Injectable()
export class HelixBookRepositoryService {
  constructor(
    private knowledgeBaseClient: KnowledgeBaseClient,
    private addCartItemService: AddCartItemService,
    private storageService: StorageService
  ) {}

  private _itemsDictionary$ = new BehaviorSubject<
    Record<KbItemType, KbItemListResponse[]>
  >({
    Analyse: [],
    Disease: [],
    GeneticDictionary: [],
  });
  private _activeTab$ = new BehaviorSubject<KbItemType>(KbItemType.Analyse);
  private _searchPhrase$ = new BehaviorSubject<string>('');
  private _activeKey$ = new BehaviorSubject<string>('');
  private _activeSubject$ = new BehaviorSubject<number | undefined>(undefined);
  isLoadedlist$ = new BehaviorSubject(false);
  scrollEvent$ = new BehaviorSubject(false);

  activeTab$ = this._activeTab$.asObservable();
  activeKey$ = this._activeKey$.asObservable();
  activeSubject$ = this._activeSubject$.asObservable();
  searchPhrase$ = this._searchPhrase$.asObservable();
  cartHxids$ = this.addCartItemService.hxids$;

  getItemsList(): Observable<KbItemListResponse[]> {
    return combineLatest([this._activeTab$, this._searchPhrase$]).pipe(
      switchMap(([type, searchPhrase]) =>
        this.getListByType(type).pipe(
          map((items) =>
            items.filter(
              (x) =>
                x.title
                  ?.toLocaleLowerCase()
                  ?.includes(searchPhrase.toLocaleLowerCase()) ||
                x.hxid?.includes(searchPhrase)
            )
          )
        )
      )
    );
  }

  getAnalyseItem(hxid: string): Observable<KbAnalyseItemResponse> {
    return this.storageService.cityId$.pipe(
      filter((cityId): cityId is number => !!cityId),
      switchMap((cityId) =>
        this.knowledgeBaseClient.getKbAnalyseItem(hxid, cityId)
      )
    );
  }

  getDiseaseItem(id: number): Observable<KbDiseaseItemResponse> {
    return this.knowledgeBaseClient.getKbDiseaseItem(id);
  }

  getGeneticDictionaryItem(
    title: string
  ): Observable<KbGeneticDictionaryItemResponse> {
    return this.knowledgeBaseClient.getKbItem(title);
  }

  addToCart(hxid: string): void {
    this.addCartItemService
      .addItemWithConfirmation(hxid, YmGoalHelixbook.ItemAddToCart)
      .pipe(first())
      .subscribe();
  }

  setTab(tab: KbItemType): void {
    this._activeTab$.next(tab);
  }

  setSearchPhrase(phrase: string): void {
    this._searchPhrase$.next(phrase);
  }

  setActiveKey(key: string): void {
    this._activeKey$.next(key);
  }

  setScrollEvent(scroll: boolean): void {
    this.scrollEvent$.next(scroll);
  }

  setActiveSubject(subjectId: number | undefined): void {
    this._activeSubject$.next(subjectId);
  }

  private getListByType(type: KbItemType): Observable<KbItemListResponse[]> {
    return this._itemsDictionary$.pipe(map((items) => items[type]));
  }

  loadList(): void {
    this._activeTab$
      .pipe(
        switchMap((type) =>
          combineLatest([of(type), this.getListByType(type)])
        ),
        tap(([, items]) => {
          if (items.length) {
            this.setActiveKey('');
          }
        }),
        filter(([, items]) => !items.length),
        tap(() => {
          this.isLoadedlist$.next(false);
        }),
        switchMap(([type]) =>
          combineLatest([
            of(type),
            this.knowledgeBaseClient.getKbItemsList(type),
          ])
        )
      )
      .subscribe(([type, items]) => {
        const currentItems = this._itemsDictionary$.getValue();
        currentItems[type] = items;
        this._itemsDictionary$.next(currentItems);
        this.setActiveKey('');
        this.isLoadedlist$.next(true);
      });
  }

  getKeyboards(items: KbItemListResponse[]): SearchPanelModel[] {
    const eng = 'abcdefghijklmnopqrstuvwxyz'.toUpperCase().split('');
    const rus = 'абвгдеёжзийклмнопрстуфхцчшщъыьэюя'.toUpperCase().split('');
    const nums = '0123456789'.split('');
    const existingSymbols = items
      .map((x) => (x.title ? x.title[0].toUpperCase() : ''))
      .filter((value, index, arr) => !!value && arr.indexOf(value) === index);

    return [
      {
        title: 'Цифры',
        keys: nums.map((x) => {
          return {
            symbol: x,
            disabled: !existingSymbols.includes(x),
          };
        }),
      },
      {
        title: 'Английский',
        keys: eng.map((x) => {
          return {
            symbol: x,
            disabled: !existingSymbols.includes(x),
          };
        }),
      },
      {
        title: 'Русский',
        keys: rus.map((x) => {
          return {
            symbol: x,
            disabled: !existingSymbols.includes(x),
          };
        }),
      },
    ].filter((keyboard) => keyboard.keys.some((key) => !key.disabled));
  }
}
